import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableRestaurant, EventSeat, DeliveryDining, LocalPhone, Close } from "@mui/icons-material";
import {
  selectTable,
  selectTables,
  selectZones,
  selectOrders,
  discardOrder,
  TABLE_STATUS,
  ITEM_STATUS,
} from "../../Store/Slices/restaurantSlice";
import { msSince, parseApiDate } from "../../Utils/apiDate";
import styles from "./Restaurant.module.css";
// How long an item may sit un-sent before the card starts asking for
// attention, and how long a fully served table waits before it nags to be
// settled.
const UNSENT_BLINK_AFTER_MS = 2 * 60 * 1000;
const SETTLE_BLINK_AFTER_MS = 20 * 60 * 1000;

const emptyState = () => ({
  activeCount: 0,
  servedCount: 0,
  hasUnsent: false,
  hasInKitchen: false,
  hasReady: false,
  oldestUnsentAt: null,
  lastServedAt: null,
  orderHeadId: null,
});

const earliest = (current, next) => {
  if (!next) return current;
  if (!current) return next;
  return parseApiDate(next) < parseApiDate(current) ? next : current;
};

const latest = (current, next) => {
  if (!next) return current;
  if (!current) return next;
  return parseApiDate(next) > parseApiDate(current) ? next : current;
};

const TableGrid = () => {
  const dispatch = useDispatch();
  const tables = useSelector(selectTables);
  const zones = useSelector(selectZones);
  const orders = useSelector(selectOrders);
  const [activeZone, setActiveZone] = useState("All");
  const [showAll, setShowAll] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  // Online and phone orders created on this terminal before the server knows
  // about them are the one case that runs off local statuses.
  const localTableState = (table) => {
    const entry = emptyState();

    (orders[table.id]?.items || []).forEach((line) => {
      if (line.status === ITEM_STATUS.SERVED) {
        entry.servedCount += 1;
        entry.lastServedAt = latest(entry.lastServedAt, line.servedAt);
        return;
      }

      entry.activeCount += 1;

      if (line.status === ITEM_STATUS.DRAFT) {
        entry.hasUnsent = true;
        entry.oldestUnsentAt = earliest(entry.oldestUnsentAt, line.addedAt);
      } else if (
        line.status === ITEM_STATUS.READY ||
        line.status === ITEM_STATUS.SERVING
      ) {
        entry.hasReady = true;
      } else {
        entry.hasInKitchen = true;
      }
    });

    return entry;
  };

  // The table list API reports itemCounts per table, already scoped to the
  // open order head, which is everything the card needs - no second request,
  // no reconstruction from ticket rows, no per-terminal bookkeeping.
  const tableState = (table) => {
    // No open order means nothing to show. This is what drops the card back to
    // plain once the order is closed after Complete Order.
    if (!table.activeOrderHeadId) {
      return table.isOnline ? localTableState(table) : emptyState();
    }

    const counts = table.itemCounts;

    // Older API, or a virtual order the summary does not cover yet.
    if (!counts) {
      return table.isOnline ? localTableState(table) : emptyState();
    }

    const count = (value) => Number(value) || 0;

    // cancelled / rejected / refunded are reported separately and are finished
    // business, so they are simply not read here.
    const pending = count(counts.pending);
    const inKitchen = count(counts.sentToKitchen) + count(counts.preparing);
    const ready = count(counts.ready) + count(counts.serving);

    return {
      activeCount: pending + inKitchen + ready,
      servedCount: count(counts.served),
      hasUnsent: pending > 0,
      hasInKitchen: inKitchen > 0,
      hasReady: ready > 0,
      oldestUnsentAt: table.oldestPendingAt || null,
      lastServedAt: table.allServedAt || null,
      orderHeadId: String(table.activeOrderHeadId),
    };
  };

  const isFullyServed = (state) =>
    state.activeCount === 0 && state.servedCount > 0;

  // Resolves a card to exactly one visual state, so nothing competes:
  //   ready        -> blinking green   (something is waiting to be served)
  //   unsent 2min+ -> blinking orange  (not sent to the kitchen yet)
  //   unsent fresh -> static orange
  //   in kitchen   -> static orange    (cooking; nothing for the till to do)
  //   served 20min+-> blinking blue    (settle the bill)
  //   served       -> static green
  const cardStateClass = (table) => {
    const state = tableState(table);
    const age = (ts) => msSince(ts, now);

    if (state.activeCount) {
      if (state.hasReady) return styles.tableReadyBlink;

      if (state.hasUnsent) {
        return age(state.oldestUnsentAt) > UNSENT_BLINK_AFTER_MS
          ? styles.tableUnsentBlink
          : styles.tableUnsentIdle;
      }

      return styles.tableUnsentIdle;
    }

    // Nothing outstanding on the table's current order means everything has
    // been served. Once that order is closed there is no state at all, which
    // is what stops the blink after settling and completing the order.
    if (!isFullyServed(state)) return "";

    // Already paid and just waiting to be closed: no need to nag. The server's
    // paidAt is authoritative so every device agrees; the local flag only
    // covers the moment between settling here and the next table refresh.
    if (table.serverPaidAt || orders[table.id]?.paidAt) {
      return styles.tableServedIdle;
    }

    // allServedAt comes from the server, so the countdown is the same on every
    // device. No value means no nudge - quiet is the right failure mode.
    return age(state.lastServedAt) > SETTLE_BLINK_AFTER_MS
      ? styles.tableSettleBlink
      : styles.tableServedIdle;
  };

  const zoneOptions = useMemo(() => ["All", ...zones], [zones]);

  const isUtilized = (table) =>
    table.isOnline ||
    Boolean(table.activeOrderHeadId) ||
    // A paid order is still on the floor until it is closed.
    Boolean(orders[table.id]?.paidAt) ||
    (orders[table.id]?.items?.length || 0) > 0;

  const visibleTables = useMemo(() => {
    const filtered =
      activeZone === "All"
        ? tables
        : tables.filter((table) => table.zone === activeZone);

    const scoped = showAll ? filtered : filtered.filter(isUtilized);

    return scoped
      .slice()
      .sort((a, b) => Number(isUtilized(b)) - Number(isUtilized(a)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, activeZone, orders, showAll]);

  const counts = useMemo(() => {
    const available = tables.filter((t) =>
      t.available !== undefined ? t.available : t.status === TABLE_STATUS.AVAILABLE,
    ).length;
    return {
      available,
      occupied: tables.length - available,
      total: tables.length,
    };
  }, [tables]);

  return (
    <div className={styles.tablePane}>
      <div className={styles.tableHeader}>
        <div>
          <h2 className={styles.paneTitle}>Select a Table</h2>
          <p className={styles.paneSubtitle}>
            {counts.available} available · {counts.occupied} occupied ·{" "}
            {counts.total} total
          </p>
        </div>
        <div className={styles.legend}>
          <span className={`${styles.legendDot} ${styles.legendAvailable}`} />
          Available
          <span className={`${styles.legendDot} ${styles.legendOccupied}`} />
          Occupied
        </div>
      </div>

      <div className={styles.zoneRow}>
        <button
          className={`${styles.zoneChip} ${
            showAll ? styles.zoneChipActive : ""
          }`}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? "Show Active Only" : "Show All Tables"}
        </button>
        {zoneOptions.map((zone) => (
          <button
            key={zone}
            className={`${styles.zoneChip} ${
              activeZone === zone ? styles.zoneChipActive : ""
            }`}
            onClick={() => setActiveZone(zone)}
          >
            {zone}
          </button>
        ))}
      </div>

      <div className={styles.tableGrid}>
        {visibleTables.map((table) => {
          const order = orders[table.id];
          const itemCount = order
            ? order.items.reduce((sum, line) => sum + line.qty, 0)
            : 0;
          const stateClass = cardStateClass(table);

          // A paid-but-unclosed order still occupies the table, whatever the
          // backend reports for availability.
          const isAvailable = order?.paidAt
            ? false
            : table.available !== undefined
              ? table.available
              : table.status === TABLE_STATUS.AVAILABLE;

          return (
            <button
              key={table.id}
              className={`${styles.tableCard} ${
                table.isOnline ? styles.tableCardOnline : (isAvailable ? styles.tableAvailable : styles.tableOccupied)
              } ${stateClass}`}
              onClick={() => dispatch(selectTable(table.id))}
            >
              {table.isOnline && (
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.tableCloseBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(discardOrder(table.id));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      dispatch(discardOrder(table.id));
                    }
                  }}
                  aria-label="Discard online order"
                >
                  <Close fontSize="inherit" />
                </span>
              )}
              {table.isPhone ? (
                <LocalPhone className={styles.tableIcon} />
              ) : table.isOnline ? (
                <DeliveryDining className={styles.tableIcon} />
              ) : (
                <TableRestaurant className={styles.tableIcon} />
              )}
              {table.isPhone ? (
                <>
                  <span className={styles.onlinePlatform}>
                    {table.customerName || "Phone"}
                  </span>
                  <span className={styles.onlineOrderNo}>{table.phone}</span>
                </>
              ) : table.isOnline ? (
                <>
                  <span className={styles.onlinePlatform}>{table.platform}</span>
                  <span className={styles.onlineOrderNo}>#{table.orderNo}</span>
                </>
              ) : (
                <span className={styles.tableName}>{table.name}</span>
              )}
              {table.isOnline ? null : (
                <span className={styles.tableSeats}>
                  <EventSeat fontSize="inherit" /> {table.seats}
                </span>
              )}
              {order?.paidAt && (
                <span className={styles.tablePaidBadge}>Paid</span>
              )}
              {itemCount > 0 && (
                <span className={styles.tableBadge}>{itemCount}</span>
              )}
              {table.reservation && (
                <span className={styles.tableReservedBadge}>
                  {table.reservation.date} {table.reservation.time}
                  {table.reservation.guests
                    ? ` · ${table.reservation.guests}p`
                    : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TableGrid;
